/**
 * Why automatic refreshes of the Authelia session are held back, kept in
 * `localStorage` so that it outlives the page: every page load would
 * otherwise ask the backend to refresh again, and the backend Authelia —
 * the cause of the 2026-10-17 lockout (see `waadooAuthConnector.ts`).
 *
 * - `expired`: Authelia refused the refresh token (`invalid_grant`, or any
 *   refusal of a refresh that was tried). Held until the user clicks the
 *   sign-in page's button.
 * - `noSession`: no refresh cookie at all (never signed in on this browser,
 *   or removed by the backend). Held until the user clicks too; shows
 *   nothing.
 * - `signedOut`: the user signed out. Same; the sign-in page says so.
 * - `rateLimited`: Authelia limits the token endpoint (503 from the
 *   backend's guard). Held until `until` — the guard's `Retry-After`, or
 *   `DEFAULT_RATE_LIMIT_MS` (60 s) without one. The sign-in page's button is
 *   disabled until then, so a click there no longer ends it early: signing
 *   in sooner would only hit Authelia again while it still refuses, and
 *   prolong the lockout. Only the in-app login dialog (`onAuthRequest` of
 *   the connector) still clears it on a click.
 */
export type RefreshBlock =
  | { reason: 'expired' | 'noSession' | 'signedOut' }
  | { reason: 'rateLimited'; until: number };

export const REFRESH_BLOCK_KEY = 'waadoo-auth:refresh-blocked';

/**
 * How long to wait for Authelia's rate limit when nothing says how long: a
 * 503 of the backend's guard without a usable `Retry-After`, and a redirect
 * that comes back with Authelia's `temporarily_unavailable`. The same 60 s as
 * the guard's default cool-down (`waadooAuthGuard.cooldownSeconds`,
 * `packages/backend/src/extensions/authRefreshGuard.ts`).
 */
export const DEFAULT_RATE_LIMIT_MS = 60_000;

function isRefreshBlock(value: unknown): value is RefreshBlock {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const { reason, until } = value as { reason?: unknown; until?: unknown };
  if (reason === 'rateLimited') {
    return typeof until === 'number';
  }
  return (
    reason === 'expired' || reason === 'noSession' || reason === 'signedOut'
  );
}

export class RefreshLatch {
  constructor(
    private readonly storage: Pick<
      Storage,
      'getItem' | 'setItem' | 'removeItem'
    >,
    // Read at each call, not `Date.now` itself: a reference taken when the
    // module loads keeps the clock of that moment's `Date` (jest's fake
    // timers install theirs later).
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** The current block; a `rateLimited` one past its `until` is over. */
  read(): RefreshBlock | undefined {
    const raw = this.storage.getItem(REFRESH_BLOCK_KEY);
    if (raw === null) {
      return undefined;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = undefined;
    }
    if (!isRefreshBlock(parsed)) {
      this.storage.removeItem(REFRESH_BLOCK_KEY);
      return undefined;
    }
    if (parsed.reason === 'rateLimited' && parsed.until <= this.now()) {
      this.storage.removeItem(REFRESH_BLOCK_KEY);
      return undefined;
    }
    return parsed;
  }

  block(block: RefreshBlock): void {
    this.storage.setItem(REFRESH_BLOCK_KEY, JSON.stringify(block));
  }

  /** The user asked to sign in: automatic refreshes may resume. */
  clear(): void {
    this.storage.removeItem(REFRESH_BLOCK_KEY);
  }
}

type LatchStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * `localStorage`, or this page's memory where it cannot be used: reading
 * `window.localStorage` throws when storage is disabled, `setItem` when it
 * is full. The fallback still holds refreshes back for the page's life.
 */
export function safeStorage(open: () => LatchStorage): LatchStorage {
  const memory = new Map<string, string>();
  const attempt = <T>(
    apply: (storage: LatchStorage) => T,
    fallback: () => T,
  ) => {
    try {
      return apply(open());
    } catch {
      return fallback();
    }
  };
  return {
    getItem: key =>
      memory.has(key)
        ? (memory.get(key) as string)
        : attempt(
            storage => storage.getItem(key),
            () => null,
          ),
    setItem: (key, value) =>
      attempt(
        storage => {
          storage.setItem(key, value);
          memory.delete(key);
        },
        () => {
          memory.set(key, value);
        },
      ),
    removeItem: key => {
      memory.delete(key);
      attempt(
        storage => storage.removeItem(key),
        () => undefined,
      );
    },
  };
}

/** The latch of this browser, shared by the auth API and the sign-in page. */
export const refreshLatch = new RefreshLatch(
  safeStorage(() => window.localStorage),
);
